import MobileBookingSummary from '@/components/booking-summary/MobileBookingSummary';
import RegularBookingSummary from '@/components/booking-summary/RegularBookingSummary';
import Footer from '@/components/Footer';
import ChangeTripsModal from '@/components/manage/change-trips/ChangeTripsModal';
import MakeChanges from '@/components/manage/make-changes/MakeChanges';
import RefundLegModal from '@/components/manage/refund/leg/RefundLegModal';
import RefundNonTripModal from '@/components/manage/refund/non-trip-offer/RefundNonTripModal';
import Tickets from '@/components/manage/tickets/Tickets';
import TravelPasses from '@/components/manage/travel-passes/TravelPasses';
import {
  clearManageFlowBooking,
  getManageFlowBookingById,
} from '@/features/manage/manageActions';
import {
  manageFlowBookingSelector,
  manageFlowTravelAccountsMapSelector,
} from '@/features/manage/manageSelectors';
import { useBookingRefundEligibleTrips } from '@/hooks/useBookingRefundEligibleTrips';
import useBookingSummaryData from '@/hooks/useBookingSummaryData';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSeparateNonTripOffersBasedOnType } from '@/hooks/useSeparateNonTripOffersBasedOnType';
import useUsedVouchers from '@/hooks/useUsedVouchers';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import { areRefundedLike } from '@/utils/admission';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { isTimestampInPast } from '@/utils/dateTime';
import { Button, Divider, Icons, Typography } from '@fleet/webshop-ui';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

interface BookingDetails {}

export const BookingDetails: FC<BookingDetails> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const booking = useSelector(manageFlowBookingSelector);
  const usedVouchers = useUsedVouchers({ bookingId: booking?.id });
  const travelAccountsMap = useSelector(manageFlowTravelAccountsMapSelector);
  const summaryData = useBookingSummaryData({
    booking,
    passengers: booking?.passengers,
    vouchers: usedVouchers.map(({ voucherCode, ...rest }) => ({
      ...rest,
      voucher_code: voucherCode,
    })),
    displayPrice: booking?.confirmedPrice,
    travelAccountsMap,
    allowedStatuses: ['FULFILLED', 'REFUNDED', 'RELEASED', 'EXCHANGED'],
  });
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isMakeChangesOpen, setIsMakeChangesOpen] = useState(false);
  const [isChangeTripsModalOpen, setIsChangeTripsModalOpen] = useState(false);
  const { refundEligibleOutboundTrips, refundEligibleInboundTrips } =
    useBookingRefundEligibleTrips(booking);
  const { travelPass, multiRide } = useSeparateNonTripOffersBasedOnType(
    booking?.nonTripOffers,
    travelAccountsMap
  );
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const { t } = useTranslation();
  const areEligibleTripsRefundable = Boolean(
    refundEligibleOutboundTrips.length || refundEligibleInboundTrips.length
  );
  const areEligibleNonTripOffersRefundable = Boolean(
    [...travelPass, ...multiRide].filter(
      ({ status, refundable, validUntil }) =>
        status !== 'REFUNDED' &&
        refundable === 'YES' &&
        (!validUntil || !isTimestampInPast(validUntil, 'Europe/Stockholm'))
    ).length
  );

  const isRefundButtonVisible =
    areEligibleTripsRefundable || areEligibleNonTripOffersRefundable;

  const isTripBooking = Boolean(booking?.bookedTrips?.length);
  const allTripAdmissions = (booking?.bookedTrips ?? []).flatMap(
    (trip) =>
      trip.bookedOffers?.flatMap((offer) => offer.admissions ?? []) ?? []
  );
  const allTripLegs = (booking?.bookedTrips ?? []).flatMap(
    (trip) => trip.legs ?? []
  );
  const isBookingRefunded =
    allTripAdmissions.length > 0 && areRefundedLike(allTripAdmissions);
  const isBookingPast =
    allTripLegs.length > 0 &&
    allTripLegs.every(
      (leg) =>
        leg.departureTime &&
        isTimestampInPast(leg.departureTime, 'Europe/Stockholm')
    );
  const isMakeChangesVisible =
    isTripBooking && !isBookingRefunded && !isBookingPast;

  // Determine badge status for travel passes
  const hasRefundedTravelPass = [...travelPass, ...multiRide].some(
    ({ status }) => status === 'REFUNDED'
  );
  const hasExpiredTravelPass = [...travelPass, ...multiRide].some(
    ({ validUntil }) =>
      validUntil && isTimestampInPast(validUntil, 'Europe/Stockholm')
  );
  const shouldShowBadge =
    !booking?.bookedTrips?.length &&
    (hasRefundedTravelPass || hasExpiredTravelPass);

  usePageTitle('manageBooking');

  // Refetch a clean slate on entry. If the user came back from an abandoned
  // change-booking checkout, `manage.booking` still holds the in-flight
  // exchange state (new trips); refetching from the BE overwrites it with the
  // real, original booking. Fires once, when the booking id first becomes
  // available — tolerating a late hydration (a hard reload of this route
  // rehydrates the persisted booking after mount). The guard ref keeps it to a
  // single fetch, since the refetch itself updates `booking`.
  const hasRefetchedRef = useRef(false);
  useEffect(() => {
    if (booking?.id && !hasRefetchedRef.current) {
      hasRefetchedRef.current = true;
      dispatch(getManageFlowBookingById(booking.id));
    }
  }, [booking?.id, dispatch]);

  const handleGoBack = useCallback(() => {
    dispatch(clearManageFlowBooking());
    navigate(-1);
  }, [dispatch, navigate]);

  return (
    <div
      className={cn('pb-16 laptop:pb-4', {
        'pb-36 laptop:pb-20': isRefundButtonVisible,
      })}
    >
      <div className="mx-auto flex max-w-laptop items-center gap-2 p-4">
        <Button
          data-testid="go-back-button"
          variant="icon"
          aria-label={t('text.goBack')}
          onClick={handleGoBack}
          className="w-fit"
        >
          <Icons.arrowLeft />
        </Button>

        <div className="flex items-baseline gap-1">
          <Typography variant={isLaptopOrBigger ? 'heading1-light' : 'body1'}>
            <>
              <TransText i18nKey="booking" />
              &#58;
            </>
          </Typography>
          <Typography variant={isLaptopOrBigger ? 'heading1' : 'subtitle'}>
            {booking?.code}
          </Typography>
        </div>
      </div>
      {shouldShowBadge ? (
        <div className="relative mx-auto max-w-laptop px-4">
          <Divider variant="medium" />
          <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 gap-1 rounded-full border border-neutral bg-white px-2 py-1 laptop:right-8">
            {hasRefundedTravelPass ? (
              <Icons.ticketReturn className="h-3 w-3 text-neutral" />
            ) : (
              <Icons.history className="h-3 w-3 text-neutral" />
            )}
            <Typography variant="label" className="text-neutral">
              <TransText
                i18nKey={hasRefundedTravelPass ? 'refunded' : 'pastBooking'}
              />
            </Typography>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-laptop px-4">
          <Divider variant="medium" />
        </div>
      )}
      <div className="mx-auto mt-8 flex max-w-laptop flex-col px-4 laptop:flex-row laptop:gap-10">
        {booking?.bookedTrips?.length ? (
          <Tickets passengersMap={summaryData.passengersMap} />
        ) : (
          <TravelPasses />
        )}
        {isLaptopOrBigger && (
          <div className="flex flex-col gap-7 pb-2">
            <Typography variant="heading2" as="h1">
              <TransText i18nKey="bookingSummary" />
            </Typography>
            <RegularBookingSummary {...summaryData} />
          </div>
        )}
      </div>
      <Footer
        content={!isLaptopOrBigger && <MobileBookingSummary {...summaryData} />}
        actionButtons={
          <>
            {isRefundButtonVisible && (
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="refund-button"
                variant="secondary"
                className="rounded-lg laptop:w-auto"
                onClick={() => setIsRefundModalOpen(true)}
                startAdornment={<Icons.ticketReturn />}
              >
                <TransText i18nKey="refund" />
              </Button>
            )}
            {isMakeChangesVisible && (
              <MakeChanges
                isOpen={isMakeChangesOpen}
                onOpenChange={setIsMakeChangesOpen}
                trigger={
                  <Button
                    size={isLaptopOrBigger ? 'medium' : 'large'}
                    data-testid="make-changes-trigger"
                    variant="secondary"
                    className="laptop:w-auto"
                    startAdornment={<Icons.ticketManage />}
                    endAdornment={
                      <Icons.chevronDown
                        className={cn(
                          'transition-transform duration-200',
                          isMakeChangesOpen && 'rotate-180'
                        )}
                      />
                    }
                  >
                    <TransText i18nKey="makeChanges" />
                  </Button>
                }
                onChangeTripsClick={() => setIsChangeTripsModalOpen(true)}
                onEditPassengerDetailsClick={() =>
                  navigate('/manage-booking/edit-passengers', {
                    state: { fromBookingDetails: true },
                  })
                }
              />
            )}
          </>
        }
      />
      <RefundLegModal
        isOpen={areEligibleTripsRefundable && isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
      />
      <RefundNonTripModal
        isOpen={areEligibleNonTripOffersRefundable && isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
      />
      <ChangeTripsModal
        isOpen={isChangeTripsModalOpen}
        onClose={() => setIsChangeTripsModalOpen(false)}
      />
    </div>
  );
};
